import React from 'react';

const MyPainHealthTerms = () => {
  return (
    <div>
      <h1>CareMappr Terms of Service - mypainhealth</h1>

      <p>
        The following agreement ("Terms of Service") constitutes an agreement
        between you and CareMappr Pty Ltd (ABN 20 669 095 550), the operator of
        the website [caremappr.com] (on its own or together with any Healthcare
        Identifier organization registered to access the My Health Record
        system, also referred as the <i>mypainhealth</i> clinician dashboard)
        (the "Site") who may also be referred to as "CareMappr", "we", "our" or
        "us". "You" means any user of the Site, which may include without
        limitation any individual user and their health care practitioner, as
        the context requires.
      </p>

      <p>
        These Terms of Service govern your use of the Site. By browsing the Site
        and/or by registering with the Site, you signify that you agree to these
        Terms of Service, including that you agree to transact with us
        electronically and that you have been provided with and consent to the
        information practices disclosed in Part 4 of these Terms of Service and
        our Privacy Policy.{' '}
        <b>
          If you do not agree to us using your personal information as set out
          in the Privacy Policy, please do not register on, or deregister
          yourself from, the Site
        </b>
      </p>

      <p>
        For Health Care Practitioners that register on the Site, by accessing
        any Individual User information via the Site, you warrant and confirm
        that the person is currently being treated by you, your access to their
        information on the Site is permitted and you will keep all such
        information strictly private and confidential and secure and only
        disclose it to the person to whom it relates (with their consent).{' '}
        <b>
          If you do not agree to these terms, please do not register on, or
          deregister yourself from, the Site.
        </b>
      </p>

      <p>
        To the extent permitted by law (including the provisions of the
        Australian Consumer Law), we offer the Site "AS IS" and without
        warranties whether express or implied.
      </p>

      <p>
        Please read these Terms of Service carefully before you use or register
        for this Site in your capacity as an individual user or health care
        practitioner.
      </p>

      <p>
        The Site is an information resource only and is not and should not be
        used as a substitute for medical advice, diagnosis or treatment.
      </p>

      <h2>1. About the Site</h2>

      <p>
        The Site displays a visualisation of information about a registered
        individual's health history based on information about the individual
        from the My Health Record system (previously known as Personally
        Controlled Electronic Health Record) and information provided by the
        individual or their health care practitioner(s) or their health care
        practitioner(s) organisation. While we upload content provided from
        various sources onto the Site, we are not the author of any of that
        content. We do not consider or assess the quality or accuracy of the
        information, nor do we accept or endorse the content of such
        information. This information is not intended to be, and should not be
        treated as, providing professional or medical advice.
      </p>

      <p>
        Everything we offer on the Site is referred to in these Terms of Service
        collectively as "the Services." To access or update your information you
        must register as an "Individual User" (for individuals using the Site
        for the purposes of their own My Health Record) or as a "Health Care
        Practitioner" (for health care practitioners or professionals accessing
        information in relation to a patient who is an Individual User). Prior
        to registering you should also review our Privacy Policy and confirm you
        are in agreement with its terms.
      </p>

      <h2>2. Changes to These Terms of Service</h2>

      <p>
        We may change these Terms of Service at any time, as we deem
        appropriate. If we make changes we will let you know by noting on the
        Site that these Terms of Service have been updated. If you disagree with
        the changes to these Terms of Service, you should discontinue your use
        of the Site and cancel your Site registration. Your ongoing use of any
        of the Services or of the Site after the changes take effect signifies
        your agreement to the new terms. We encourage you to review these Terms
        of Service and our Privacy Policy regularly.
      </p>

      <h2>3. Registration</h2>

      <p>
        When you register on the Site as an Individual User you are required to
        provide personal details such as your name, contact details, Medicare
        number and date of birth. When you register on the Site as a Health Care
        Practitioner you are required to provide personal details such as your
        name, contact details and, as applicable, registration number.
      </p>

      <p>
        These are your credentials for accessing Services that are only
        available to registered users ("Credentials").
      </p>

      <p>
        By providing your Credentials and registering on the Site, you agree
        that you:
      </p>

      <ol>
        <li>are not impersonating any person or entity;</li>
        <li>
          are not intentionally or unintentionally violating any applicable
          state or federal law regarding use of personal or identification
          information;
        </li>
        <li>
          will provide, on demand by us, verification of your identity and
          address in such form as required by us;
        </li>
        <li>
          for Health Care Practitioners, will only access information of
          Individual Users that share their Credentials with you and that you
          are currently treating; and
        </li>
        <li>
          for Individual Users, consent to us (and any Healthcare Identifier
          organization working with us) using your Credentials to access your
          health data stored in your My Health Record and otherwise in
          accordance with the Privacy Policy and for your health data to be
          displayed via the Site and Services to you and others you permit to
          access it (such as your Health Care Practitioner(s) registered on the
          Site).
        </li>
      </ol>

      <p>
        The Site and the Services are offered only to users 18 years of age or
        older. If you are under 18 years old, please do not use the Site and/or
        the Services. Any person who registers on the Site represents to us that
        they are 18 years of age or older.
      </p>

      <p>
        If you intend to use the Site as a health care practitioner, you must
        register as a "Health Care Practitioner". You warrant and represent that
        the information you provide via the Site is (and will remain) accurate,
        true and correct and that you will inform us as soon as possible in the
        event there is any inaccuracy to your personal information on the Site.
      </p>

      <p>
        You shall indemnify us in accordance with clause 11 of these Terms of
        Service for any liabilities, claims, damages, loss and expenses suffered
        by us arising from your negligence or breach of these Terms of Service.
      </p>

      <h2>4. Your Personal Information</h2>

      <p>
        The CareMappr Privacy Policy (copy available on the Site) explains the
        information practices that apply to the personal information we have
        about you. If you provide information to the Site, please ensure that it
        is accurate, current and complete. You agree to maintain and update such
        information as appropriate.
      </p>

      <p>
        If you register on the Site you consent to the use and transfer of your
        personal information as set out in the Privacy Policy including for
        processing and storage by us, to any Healthcare Identifier organization
        working with us to access the My Health Record system, in an aggregated
        and de-identified form for use in research and to researchers who may
        wish to contact you regarding relevant research approved by a National
        Health and Medical Research Council (NHMRC) ethics committee.
        Additionally, you agree that we may use your Credentials to authenticate
        you on the Service or the Site.
      </p>

      <h2>5. We Do Not Provide Medical Advice</h2>

      <p>
        This Site and the Services offer a summary of an Individual User's
        health history and health information based on information from My
        Health Record and information provided by them and their health care
        practitioner. The Site and the Services are designed for information
        purposes only.
      </p>

      <p>
        You must not rely on this information as a substitute for, nor does the
        information replace, professional medical advice, diagnosis or
        treatment. The information on the Site and the Services are not intended
        to be medical advice or constitute a recommendation for treatment to any
        health care practitioner. The information should not be relied upon
        without independent verification in order to diagnose, treat, cure or
        prevent any medical condition and should not be used for therapeutic
        purposes.
      </p>

      <p>
        We do not endorse, accept or adopt the Health Care Practitioners using
        the Site, or any other health care practitioners, including specialist
        practitioners.
      </p>

      <p>
        If you have any concerns or questions about your own health based on
        information you have seen on the Site, you should always take your own
        steps to seek out and consult with a suitably qualified health care
        practitioner. Do not disregard, avoid or delay obtaining medical or
        health-related advice from your health care practitioner because of
        something you may have read on this Site. Nothing stated or posted on
        this Site or available through any Services is intended to be, and must
        not be taken to be, the practice of medical or counselling care.
      </p>

      <p>
        Your access to and use of this Site and the Services does not create in
        any way a practitioner/patient, confidential or privileged relationship,
        or any other relationship that would give rise to any duties on our
        part.
      </p>

      <p>
        We do not recommend or endorse any specific tests, health care
        practitioners, procedures, opinions or other information that may appear
        on the Site. If you rely on any of the information provided by this
        Site, its employees, or its guests or visitors, you do so solely at your
        own risk.
      </p>

      <h2>6. Your Responsibilities</h2>

      <p>
        You are responsible for your use of the Site and Services, including use
        by others to whom you have given your Credentials and/or permitted to
        access your information on the Site. You are responsible for keeping
        your password confidential and for notifying us if your password has
        been hacked or stolen.
      </p>

      <p>
        You may use the Site and Services for lawful purposes only. You may not
        use the Site in any manner that could damage, disable, overburden or
        impair our servers or networks, or interfere with any other party's use
        and enjoyment of the Site and/or Services. You may not attempt to gain
        unauthorised access to any Services, user accounts or computer systems
        or networks, through hacking, password mining or any other means. We may
        take any legal and technical remedies to prevent the violation of this
        provision and to enforce these Terms of Service.
      </p>

      <h2>7. We Make No Warranties</h2>

      <p>
        We provide the Site and the Services "as is", "with all faults" and "as
        available." To the extent permitted by law. We and our suppliers make no
        express or implied warranties, representations or guarantees about the
        Site and Services or respect of the information on the Site and the
        Services, including but not limited to the completeness, currency,
        availability, reliability, suitability and/or accuracy of any
        information on the Site. We and our suppliers do not guarantee that the
        results that may be obtained from the use of the Services will be
        effective, reliable, accurate or meet your requirements.{' '}
        <b>
          However, nothing in this clause or these Terms of Service is intended
          to limit or exclude any non-excludable rights or consumer guarantees
          under the Australian Consumer Law.
        </b>
      </p>

      <p>
        We do not guarantee that you will be able to access or use the Site
        and/or the Services (either directly or through third-party networks) at
        times or locations of your choosing. No oral or written information
        given by a CareMappr representative shall create a warranty. You use the
        Site and the Services at your own risk.
      </p>

      <h2>8. Security</h2>

      <p>
        We take reasonable steps to secure the Site. However, you acknowledge
        that there are risks that information which is sent to or from the Site
        may be modified, intercepted or corrupted by third parties. Furthermore,
        you acknowledge that there is a risk that material downloaded from the
        Site may contain viruses, worms or other defects or devices. We accept
        no liability for any damages to or interference with your computer or
        device used in connection with the Site. You agree that your use of the
        Site and/or Services is solely at your own risk.
      </p>

      <h2>9. General Limitation of Liability</h2>

      <p>
        You agree that your use of any information on the Site and/or the
        Services is at your own risk entirely and that the Site may contain
        inaccuracies or errors.
      </p>

      <p>
        To the maximum extent permitted by law, your sole and exclusive remedy
        for any dispute with us or our contributors is re-performance of the
        Services. We shall not be liable for any indirect, special, incidental
        or consequential loss (including loss or profit, opportunity or revenue)
        or exemplary damages arising from your use of or inability to use the
        Site and/or Services.
      </p>

      <h2>10. Termination</h2>

      <p>
        We may terminate and/or suspend your registration immediately, without
        notice, if there has been a violation of these Terms of Service, or
        other policies and terms posted on the Site, by you or anyone using your
        Credentials.
      </p>

      <p>
        We reserve the right to terminate or suspend your registration at any
        time should we form the view, in our absolute discretion, that our or
        the Site's reputation, brand, operations (including the website and
        users) or business (including our partners and affiliates) may be
        affected or harmed as a result of your registration on the Site.
      </p>

      <p>
        We may also terminate or suspend your registration on any other grounds
        we consider reasonable, in our absolute discretion, including if you
        have failed to provide any consent or authorisations reasonably
        requested by us and required by law or good practice in order for us to
        provide the Services, or if we are notified that you may have engaged in
        identity theft or fraud or due to inactivity for an extended period, but
        we will attempt to notify you in advance of such cancellation or
        suspension.
      </p>

      <p>
        Your right to use the Services will end once your registration is
        cancelled or terminated, and any data you have stored on such a Service
        may be unavailable later, unless we are required to retain it by law
        including for the purposes of making a report to police in relation to
        identity theft or fraud.
      </p>

      <p>
        You may terminate your registration on the Site at any time. We
        recommend that you transfer any records or other information stored on
        the Site prior to terminating your registration.
      </p>

      <h2>11. Indemnification</h2>

      <p>
        To the maximum extent permitted by law, you agree to defend, indemnify
        and hold harmless us, our employees, contractors, officers, directors,
        agents, parent, other affiliated companies and suppliers, from all
        liabilities, claims, damages, loss and expenses, including attorney's
        fees, that arise from your use or misuse of the Site and the Services
        including, but not limited to, your reliance on any information
        presented on the Site and your posted content, or for your negligence or
        breach of these Terms of Service. We reserve the right to assume control
        of the defence of any third party claim that is subject to
        indemnification by you, in which event you will cooperate with us in
        asserting any available defences.
      </p>

      <h2>12. Electronic Contracting and Notices</h2>

      <p>
        By using this Site and/or registering for the Site or Services, you
        accept these Terms of Service together with our Privacy Policy (the
        "Agreement"). You also agree that we may send to you in electronic form
        any privacy or other notices, disclosures, reports, documents,
        communications or other records regarding the Site and/or the Services
        (collectively, "Notices"). We can send electronic Notices to the e-mail
        address that you provided to us during registration. Alternatively we
        may post the Notice on the applicable Service or otherwise through the
        Site. Notice will be deemed given twenty-four (24) hours after such
        email is sent or notice is posted. You can withdraw your consent to
        receive Notices electronically by cancelling or discontinuing your use
        of the applicable Service.
      </p>

      <p>
        In order to receive Notices electronically, you must have a personal
        computer with an internet connection. You can retrieve an electronic
        copy and a printable version of this agreement by clicking on the "Terms
        of Service" link on any web page that hosts any of the Services. All
        contracts completed electronically will be deemed for all legal purposes
        to be in writing and legally enforceable as a signed writing.
      </p>

      <h2>13. Entire Agreement</h2>

      <p>
        The Agreement and any supplemental terms, policies, rules and guidelines
        posted on the Site and/or the Services constitute the entire agreement
        between you and us and supersede all previous written or oral
        agreements. If any part of the Agreement is held invalid or
        unenforceable, that portion shall be construed in a manner consistent
        with applicable law to reflect, as nearly as possible, the original
        intentions of the parties, and the remaining portions shall remain in
        full force and effect.
      </p>

      <h2>14. Choice of Law and Location for Resolving Disputes</h2>

      <p>
        You agree that the law of the State of South Australia, Australia
        governs these Terms of Service and you submit to the exclusive
        jurisdiction of the courts of South Australia in respect of any claim or
        dispute in connection with the Services or these Terms of Service. You
        further agree that any disputes or claims that you may have against us
        will be resolved by a court located in the State of South Australia.
      </p>

      <h2>15. Relationship of the Parties</h2>

      <p>
        The parties are independent contractors with respect to each other. This
        Agreement does not constitute and shall not be construed as constituting
        a partnership or joint venture among the parties hereto, or an
        employee-employer relationship. You agree that we have no special
        relationship with or fiduciary duty to you. Neither party shall have any
        right, power or authority to enter into any agreement for, or on behalf
        of, or incur any obligation or liability of, or to otherwise bind, the
        other party.
      </p>

      <h2>16. General</h2>

      <p>
        The failure by us to exercise or enforce any right or provision of this
        Agreement does not constitute a waiver of such right or provision. If
        any provision of this Agreement is found by a court of competent
        jurisdiction to be invalid, the parties nevertheless agree that the
        court should try to give effect to the parties' intentions as reflected
        in the provision, and the other provisions of this Agreement remain in
        full force and effect.
      </p>

      <p>
        This Agreement constitutes the entire agreement between you and us and
        governs your use of the Site, superseding any prior agreements (whether
        written or oral) between you and us regarding the subject matter hereof.
        Neither party is relying upon any warranties, representations,
        assurances or inducements not expressly set forth in this Agreement.
      </p>

      <p>
        The terms "including" and "includes" shall be deemed to be followed by
        the statement "without limitation" and neither of these terms shall be
        construed to limit any word or statement it follows to the specific or
        similar terms or matters immediately following it.
      </p>

      <h2>17. Intellectual Property</h2>

      <p>
        All copyright, trademarks and all other intellectual property rights in
        the Site and its content included in the Site and all software and
        source codes connected with the Site (<b>Intellectual Property</b>) is
        owned by us. Any use of the Intellectual Property without our express
        permission is prohibited.
      </p>

      <h2>18. Contact</h2>

      <p>You may contact us in the following ways:</p>

      <p>
        Email: <i>privacy@caremappr.com.au</i>
      </p>
    </div>
  );
};

export default MyPainHealthTerms;
